'use client';

import { c as _c } from "react/compiler-runtime";
import Player from '@vimeo/player';
import clsx from 'clsx';
import { FC, useEffect, useRef, useState } from 'react';
import { preconnect } from 'react-dom';
import cn from '@/helpers/cn';
import css from './style.module.scss';
export interface Props {
  fitCover?: boolean;
  autopause?: boolean;
  background?: boolean;
  className?: string;
  loop?: boolean;
  muted?: boolean;
  playing?: boolean;
  responsive?: boolean;
  thumbnailUrl?: string;
  thumbnailAlt?: string;
  url: string;
  preload?: 'auto' | 'metadata' | 'metadata_on_hover' | 'auto_on_hover' | 'none';
  controls?: boolean;
  disablePointerEvents?: boolean;
  thumbnailLoading?: 'lazy' | 'eager';
}
const extractVimeoId = (url: string): string | null => {
  const match = url.match(/vimeo\.com\/(?:manage\/videos\/)?(\d+)/);
  return match ? match[1] : null;
};
const VimeoPlayer: FC<Props> = t0 => {
  const $ = _c(39);
  const {
    autopause: t1,
    fitCover,
    thumbnailUrl,
    thumbnailAlt: t2,
    background: t3,
    className: t4,
    loop: t5,
    muted: t6,
    playing: t7,
    responsive: t8,
    url,
    preload,
    controls: t9,
    disablePointerEvents: t10,
    thumbnailLoading: t11
  } = t0;
  const autopause = t1 === undefined ? false : t1;
  const thumbnailAlt = t2 === undefined ? "" : t2;
  const background = t3 === undefined ? true : t3;
  const className = t4 === undefined ? "" : t4;
  const loop = t5 === undefined ? true : t5;
  const muted = t6 === undefined ? false : t6;
  const playing = t7 === undefined ? true : t7;
  const responsive = t8 === undefined ? true : t8;
  const controls = t9 === undefined ? false : t9;
  const disablePointerEvents = t10 === undefined ? true : t10;
  const thumbnailLoading = t11 === undefined ? "lazy" : t11;
  preconnect("https://player.vimeo.com");
  preconnect("https://f.vimeocdn.com");
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const playerRef = useRef(null);
  const canPlayRef = useRef(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const [showCoverImage, setShowCoverImage] = useState(!!thumbnailUrl);
  const [shouldInit, setShouldInit] = useState(false);
  let t12;
  let t13;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      const el = wrapperRef.current;
      if (!el) {
        return;
      }
      const observer = new IntersectionObserver(t14 => {
        const [entry] = t14;
        if (entry.isIntersecting) {
          setShouldInit(true);
          observer.disconnect();
        }
      }, {
        rootMargin: "400px"
      });
      observer.observe(el);
      return () => observer.disconnect();
    };
    t13 = [];
    $[0] = t12;
    $[1] = t13;
  } else {
    t12 = $[0];
    t13 = $[1];
  }
  useEffect(t12, t13);
  let t14;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = async () => {
      if (!wrapperRef.current || !containerRef.current || !playerRef.current) {
        return;
      }
      const [width, height] = await Promise.all([playerRef.current.getVideoWidth(), playerRef.current.getVideoHeight()]);
      if (!wrapperRef.current || !containerRef.current) {
        return;
      }
      const wrapperAspectRatio = wrapperRef.current.clientWidth / wrapperRef.current.clientHeight;
      const defaultVideoAspectRatio = width / height;
      const scale = Math.max(defaultVideoAspectRatio / wrapperAspectRatio, wrapperAspectRatio / defaultVideoAspectRatio);
      containerRef.current.style.transform = `scale(${scale})`;
    };
    $[2] = t14;
  } else {
    t14 = $[2];
  }
  const fitToAspectRatio = t14;
  let t15;
  if ($[3] !== autopause || $[4] !== background || $[5] !== controls || $[6] !== fitCover || $[7] !== loop || $[8] !== muted || $[9] !== playing || $[10] !== preload || $[11] !== responsive || $[12] !== shouldInit || $[13] !== url) {
    t15 = () => {
      if (containerRef.current === null || shouldInit === false) {
        return;
      }
      const vimeoId = extractVimeoId(url);
      if (!vimeoId) {
        console.error("Invalid Vimeo URL:", url);
        return;
      }
      const numericVimeoId = parseInt(vimeoId, 10);
      if (isNaN(numericVimeoId)) {
        throw new Error(`Invalid Vimeo ID: ${vimeoId}`);
      }
      const player = new Player(containerRef.current, {
        id: numericVimeoId,
        loop,
        muted,
        background: controls ? false : background,
        responsive,
        colors: controls ? ["000000", "9bff00", "FFFFFF", "000000"] : undefined,
        autopause,
        autoplay: playing,
        preload,
        controls,
        title: false,
        byline: false,
        portrait: false,
        dnt: true
      });
      playerRef.current = player;
      if (fitCover) {
        fitToAspectRatio();
      }
      player.on("play", async () => {
        if (background && !canPlayRef.current) {
          await player.pause();
          canPlayRef.current = true;
        }
        setIsPlayerReady(true);
        if (!controls) {
          setShowCoverImage(false);
        }
      });
      if (!controls) {
        player.on("pause", () => {
          setShowCoverImage(true);
          setTimeout(async () => {
            const isPaused = await player?.getPaused();
            if (isPaused) {
              player.setCurrentTime(0);
            }
          }, 500);
        });
      }
      return () => {
        player.destroy();
      };
    };
    $[3] = autopause;
    $[4] = background;
    $[5] = controls;
    $[6] = fitCover;
    $[7] = loop;
    $[8] = muted;
    $[9] = playing;
    $[10] = preload;
    $[11] = responsive;
    $[12] = shouldInit;
    $[13] = url;
    $[14] = t15;
  } else {
    t15 = $[14];
  }
  let t16;
  if ($[15] !== controls || $[16] !== loop || $[17] !== muted || $[18] !== shouldInit || $[19] !== url) {
    t16 = [url, loop, muted, shouldInit, controls];
    $[15] = controls;
    $[16] = loop;
    $[17] = muted;
    $[18] = shouldInit;
    $[19] = url;
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  useEffect(t15, t16);
  let t17;
  let t18;
  if ($[21] !== isPlayerReady || $[22] !== playing) {
    t17 = () => {
      if (playerRef.current === null || isPlayerReady === false || playing === undefined) {
        return;
      }
      if (playing) {
        playerRef.current.play().catch(_temp);
      } else {
        playerRef.current.pause().catch(_temp2);
      }
    };
    t18 = [playing, isPlayerReady];
    $[21] = isPlayerReady;
    $[22] = playing;
    $[23] = t17;
    $[24] = t18;
  } else {
    t17 = $[23];
    t18 = $[24];
  }
  useEffect(t17, t18);
  let t19;
  if ($[25] !== className) {
    t19 = cn(css.wrapper, className);
    $[25] = className;
    $[26] = t19;
  } else {
    t19 = $[26];
  }
  const t20 = showCoverImage ? 0 : 1;
  let t21;
  if ($[27] !== t20) {
    t21 = <div ref={containerRef} className={css.player} style={{
      opacity: t20
    }} />;
    $[27] = t20;
    $[28] = t21;
  } else {
    t21 = $[28];
  }
  let t22;
  if ($[29] !== disablePointerEvents || $[30] !== showCoverImage || $[31] !== thumbnailAlt || $[32] !== thumbnailLoading || $[33] !== thumbnailUrl) {
    t22 = thumbnailUrl ? <img alt={thumbnailAlt} decoding="async" draggable={false} fetchPriority={thumbnailLoading === "eager" ? "high" : "low"} loading={thumbnailLoading} src={thumbnailUrl} className={clsx(css.thumbnail, disablePointerEvents && css.disablePointerEvents)} style={{
      opacity: showCoverImage ? 1 : 0
    }} /> : null;
    $[29] = disablePointerEvents;
    $[30] = showCoverImage;
    $[31] = thumbnailAlt;
    $[32] = thumbnailLoading;
    $[33] = thumbnailUrl;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== t19 || $[36] !== t21 || $[37] !== t22) {
    t23 = <div ref={wrapperRef} className={t19}>{t21}{t22}</div>;
    $[35] = t19;
    $[36] = t21;
    $[37] = t22;
    $[38] = t23;
  } else {
    t23 = $[38];
  }
  return t23;
};
export default VimeoPlayer;
function _temp(error) {
  console.warn("Failed to play video:", error.name);
}
function _temp2(error_0) {
  console.warn("Failed to pause video:", error_0.name);
}